import React from "react";
import { Table } from "react-bootstrap";
import styled from "styled-components";
import ItemIcon from "../../../../../images/placeholders/item-muted-grey.svg";
import DonationIcon from "../../../../../images/placeholders/heart-muted-grey.svg";
import CheckoutIcon from "../../../../../images/placeholders/checkout-muted-grey.svg";
import SubscriptionIcon from "../../../../../images/placeholders/subscription-muted-grey.svg";
import InvoicesIcon from "../../../../../images/placeholders/invoices-muted-grey.svg";
import CartIcon from "../../../../../images/placeholders/cart-muted-grey.svg";
import DollarIcon from "../../../../../images/placeholders/dollar-circle-muted-grey.svg";
import ContactIcon from "../../../../../images/placeholders/profile-muted-grey.svg";
import {
  AuthService,
  TableData,
  TableHead,
  TableLayout,
  TablePagination,
  TableRow,
  TBody,
  THead,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import CustomPlaceholders from "./CustomPlaceholders";
import { GridNames } from "../enums";
import UpsertPaymentPopUp from "./UpsertPaymentPopUp";
import NewSubscriptionPopUp from "./NewSubscriptionPopUp";
import UpsertInvoicePopUp from "./UpsertInvoicePopUp";
import NewContactPopup from "./NewContactPopup";
import {
  SubscriptionModel,
  UpsertInvoiceModel,
  UpsertPaymentModel,
} from "../models";
import { ITableGrid } from "../interfaces/ITableGrid";

export const InputWrapper = styled.div`
  /* height: inherit; // if its not there then creating issue for placeholder */
  .table-with-header-btns {
    border: 1px solid #ddd;
    border-radius: 10px;
    padding: 20px;
    margin: 20px 0px 20px;
    background: #fff;
    table {
      border: 0px;
      label {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        width: 100%;
      }
      thead {
        margin-bottom: 10px;
      }
      tbody {
        min-height: 30px;
        height: auto;
      }
      tr:last-child {
        td {
          border-bottom: 0px;
        }
      }
    }
    .variant-table {
      td,
      th {
        vertical-align: middle;
        &:first-child {
          padding-left: 0px;
          width: 160px;
        }
        img {
          width: 22px;
          height: 22px;
        }
        &.related-media-column {
          img {
            height: 30px;
            width: 30px;
          }
        }
      }
      th {
        &:first-child {
          text-align: left;
          padding-left: 25px;
          position: sticky;
        }
      }

      td {
        &:first-child {
          padding-left: 25px;
          position: sticky;
        }
        &:last-child {
          width: 80px;
          max-width: 80px;
          min-width: 80px;
        }
      }
    }
    &.table-header-max-height {
      table {
        tbody {
          max-height: 165px;
        }
      }
    }
  }
  .plh-on-search {
    &.search-border-none {
      .plh-main-section {
        border: none;
      }
    }
  }
  &.height-inherit {
    height: inherit;
  }
  .text-center-pr-30 {
    text-align: center;
    padding-right: 30px;
  }
  .text-right-pr-30 {
    text-align: right;
    padding-right: 30px;
  }
`;

export interface ICustomPlaceholdersProps {
  image?: string;
  label?: string;
  description?: React.ReactNode;
  showItems?: boolean;
  height?: string;
  className?: string;
}

const gridConfigs = (key: keyof typeof GridNames): ICustomPlaceholdersProps => {
  switch (key) {
    case GridNames.Item:
      return {
        image: ItemIcon,
        label: "No Items",
        description: (
          <>
            Create some Items to sell. Each one is a bit different.
            <br />
            Hover over each type below to learn more.
          </>
        ),
        showItems: true,
      };
    case GridNames.Contact:
      return {
        image: ContactIcon,
        label: "No Contacts",
        description: (
          <>
            Contacts will be created when Items are sold,
            <br />
            but you can create one now.
          </>
        ),
      };
    case GridNames.Service:
      return {
        image: ItemIcon,
        label: "Service Items",
        description: (
          <>
            Create some Services to sell. Each one is a bit different.
            <br />
            Hover over each type below to learn more.
          </>
        ),
      };
    case GridNames.Donation:
      return {
        image: DonationIcon,
        label: "No Donations",
        description: (
          <>
            Donations are created when a purchase is made
            <br />
            using a Checkout Page or Purchase Link
            <br /> of a Donation Item.
          </>
        ),
      };
    case GridNames.Donors:
      return {
        image: DonationIcon,
        label: "No Donors",
        description: (
          <>
            Donors will be available only when a
            <br /> donation has been made.
          </>
        ),
      };
    case GridNames.Order:
      return {
        image: CartIcon,
        label: "No Orders",
        description: (
          <>
            Orders are created when a purchase is made using a
            <br />
            Checkout Page or Purchase Link from an Item.
          </>
        ),
      };
    case GridNames.Payment:
      return {
        image: DollarIcon,
        label: "No Payments",
        description: (
          <>
            Payments are created when a purchase is made
            <br />
            and an Invoice is paid. You can manually
            <br /> create a Payment at any time.
          </>
        ),
        // className: "db-payment-plh",
      };
    case GridNames.Subscription:
      return {
        image: SubscriptionIcon,
        label: "No Subscriptions",
        description: (
          <>
            Subscriptions are created when an Order is generated
            <br />
            that has Items with recurring billing. You can manually
            <br /> create a Subscription at any time.
          </>
        ),
      };
    case GridNames.Invoice:
      return {
        image: InvoicesIcon,
        label: "No Invoices",
        description: (
          <>
            Invoices are created when an Order is generated
            <br />
            or when a Subscription renews. You can manually <br />
            create an Invoice at any time.
          </>
        ),
      };
    case GridNames.Checkout:
      return {
        image: CheckoutIcon,
        label: "No Checkouts",
        description: (
          <>
            Checkout Pages allow you to design custom purchase experiences.
            <br />
            Choose different layouts, add multiple items
            <br /> and customize colors and branding.
          </>
        ),
      };
    case GridNames.PayOuts:
      return {
        image: ItemIcon,
        label: "No Funding Records",
        description: (
          <>
            {/* Funding entries represent sources or destinations of financial
            transactions.
            <br />
            Add a record to initiate fund tracking and allocation */}
          </>
        ),
      };
    default:
      return { image: ItemIcon, label: "No Record Found", description: <></> };
  }
};

const TableGrid = <T,>({
  data,
  cellSpacing,
  headers,
  pagination,
  onPaginationChange,
  onRowClick,
  tableHeader,
  className,
  wrapperClassName,
  height,
  name,
  hideTable = false,
  isShowPlaceholder = true,
  onRefresh,
  rolesAndPermissionStore,
  countriesWithState,
}: ITableGrid<T>) => {
  const [onOpenPopUp, setOnOpenPopUp] = React.useState("");

  const onClose = (refresh: boolean) => {
    setOnOpenPopUp("");
    if (refresh && onRefresh) {
      onRefresh();
    }
  };

  function renderPopup() {
    switch (onOpenPopUp) {
      case "payment":
        return (
          <UpsertPaymentPopUp
            onClose={(refresh: boolean) => onClose(refresh)}
            paymentData={new UpsertPaymentModel()}
            countriesWithState={countriesWithState}
          />
        );
      case "subscription":
        return (
          <NewSubscriptionPopUp
            subscriptionData={new SubscriptionModel()}
            countriesWithState={countriesWithState}
            onClose={(refresh: boolean) => onClose(refresh)}
          />
        );
      case "invoice":
        return (
          <UpsertInvoicePopUp
            onClose={(refresh: boolean) => onClose(refresh)}
            invoiceData={new UpsertInvoiceModel()}
            countriesWithState ={countriesWithState}
          />
        );
      case "contact":
        return (
          <NewContactPopup
            onClose={(refresh: boolean) => onClose(refresh)}
            countriesWithState={countriesWithState}
          />
        );
      default:
        return null;
    }
  }

  const config = gridConfigs(name as keyof typeof GridNames);
  const isPlaceholder = data.length === 0 && isShowPlaceholder && hideTable;

  return (
    <>
      {renderPopup()}
      <InputWrapper
        className={`table-input-wrapper ${isPlaceholder ? "height-inherit" : ""} `}
      >
        <TableLayout
          tableHeader={!hideTable ? tableHeader : false}
          className={wrapperClassName}
          placeholder={data.length === 0 && hideTable}
        >
          <Table
            cellSpacing={cellSpacing}
            className={`table-main ${className || ""}`}
          >
            {!hideTable && (
              <>
                <THead>
                  {headers &&
                    headers.map((header, index) => {
                      return (
                        <TableHead
                          className={header.className || ""}
                          key={index}
                        >
                          {header.title}
                        </TableHead>
                      );
                    })}
                </THead>
                <TBody
                  fromPaymentSystem={AuthService.fromPaymentSystem}
                  height={
                    height ||
                    (data.length === 0 && isShowPlaceholder
                      ? // ? "calc(100vh - 343px)" // creating issue while searching in staging
                        // : data.length > 0 && !hideTable
                        "calc(100vh - 226px)"
                      : "")
                  }
                >
                  {data &&
                    data.map((item, index) => {
                      return (
                        <TableRow
                          key={index}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRowClick && onRowClick(item);
                          }}
                        >
                          {headers &&
                            headers.map((header, index) => {
                              return (
                                <TableData
                                  onClick={(e) =>
                                    header.onCellClick &&
                                    header.onCellClick(e, item)
                                  }
                                  color={header.cellColor || ""}
                                  key={index}
                                  className={header.cellClassName || ""}
                                >
                                  {header.onCellRender
                                    ? header.onCellRender(item)
                                    : (item[
                                        header.dataKey
                                      ] as unknown as React.ReactNode)}
                                </TableData>
                              );
                            })}
                        </TableRow>
                      );
                    })}
                  {!data ||
                    (data.length === 0 && isShowPlaceholder && (
                      <CustomPlaceholders
                        image={config?.image}
                        label={config?.label}
                        description={config?.description}
                        showItems={config?.showItems}
                        onOpenPopUp
                        className={`${config?.className} plh-on-search search-border-none`}
                      />
                    ))}
                </TBody>
              </>
            )}
            {hideTable && data.length === 0 && (
              <CustomPlaceholders
                image={config?.image}
                label={config?.label}
                description={config?.description}
                showItems={config?.showItems}
                showButton={hideTable}
                gridName={name}
                onOpenPopUp={(popUpName) => {
                  if (!rolesAndPermissionStore?.hasManagePermission("Create"))
                    return;
                  setOnOpenPopUp(popUpName);
                }}
                className={`${config?.className} plh-on-search`}
              />
            )}
          </Table>
          {!hideTable && pagination && data.length > 0 && (
            <TablePagination
              onPageChange={(pageNumber: number) =>
                onPaginationChange &&
                onPaginationChange(pageNumber, pagination.pageSize)
              }
              onPageSizeChange={(pageSize: number) =>
                onPaginationChange && onPaginationChange(1, pageSize)
              }
              pagination={pagination}
              className="table-pagination"
            />
          )}
        </TableLayout>
      </InputWrapper>
    </>
  );
};
export default withRouter(TableGrid);
